import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

import Landing from "@/pages/Landing";
import ProductDetail from "@/pages/ProductDetail";
import CubeConfigurator from "@/pages/CubeConfigurator";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import PublicLayout from "@/pages/public/PublicLayout";
import RestaurantHome from "@/pages/public/RestaurantHome";
import CategorySelect from "@/pages/public/CategorySelect";
import DishList from "@/pages/public/DishList";
import ShowcaseSite from "@/pages/showcase/ShowcaseSite";
import ShowcasePrivacy from "@/pages/showcase/ShowcasePrivacy";
import BookingsPage from "@/pages/admin/BookingsPage";
import Reviews from "@/pages/public/Reviews";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import MenuEditor from "@/pages/admin/MenuEditor";
import SettingsPage from "@/pages/admin/SettingsPage";
import ReviewsPage from "@/pages/admin/ReviewsPage";
import StaffPage from "@/pages/admin/StaffPage";
import SubscriptionPage from "@/pages/admin/SubscriptionPage";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";
import { SHOW_SUBSCRIPTION_SECTION } from "@/config";
import AdminLogin from "@/pages/superadmin/AdminLogin";
import AdminPanel from "@/pages/superadmin/AdminPanel";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] text-muted-foreground">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Custom domain gate: if the visitor arrives on a mapped custom domain, serve the restaurant menu.
const PLATFORM_HOST_SUFFIXES = ["emergentagent.com", "emergent.host", "localhost"];
const RootGate = () => {
  const [state, setState] = React.useState({ checked: false, to: null });
  React.useEffect(() => {
    const host = window.location.hostname;
    const isPlatform = PLATFORM_HOST_SUFFIXES.some((s) => host === s || host.endsWith(`.${s}`) || host.endsWith(s));
    if (isPlatform) { setState({ checked: true, to: null }); return; }
    // Prima i Siti Vetrina, poi (fallback) i Menù. La risoluzione dei menù resta invariata.
    api.get(`/showcase/resolve-domain?host=${encodeURIComponent(host)}`)
      .then((r) => setState({ checked: true, to: `/s/${r.data.slug}` }))
      .catch(() => api.get(`/public/resolve-domain?host=${encodeURIComponent(host)}`)
        .then((r) => setState({ checked: true, to: `/m/${r.data.slug}` }))
        .catch(() => setState({ checked: true, to: null })));
  }, []);
  if (!state.checked) return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C]" />;
  if (state.to) return <Navigate to={state.to} replace />;
  return <Landing />;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/prodotti/:id" element={<ProductDetail />} />
      <Route path="/configura" element={<CubeConfigurator />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/m/:slug" element={<PublicLayout />}>
        <Route index element={<RestaurantHome />} />
        <Route path="menu/:menuId" element={<CategorySelect />} />
        <Route path="menu/:menuId/:categoryId" element={<DishList />} />
        <Route path="recensioni" element={<Reviews />} />
      </Route>
      <Route path="/s/:slug" element={<ShowcaseSite />} />
      <Route path="/s/:slug/privacy" element={<ShowcasePrivacy />} />
      <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="statistiche" element={<AnalyticsPage />} />
        <Route path="menu/:menuId" element={<MenuEditor />} />
        <Route path="abbonamento" element={SHOW_SUBSCRIPTION_SECTION ? <SubscriptionPage /> : <Navigate to="/admin" replace />} />
        <Route path="impostazioni" element={<SettingsPage />} />
        <Route path="recensioni" element={<ReviewsPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="prenotazioni" element={<BookingsPage />} />
      </Route>
      <Route path="/superadmin/login" element={<AdminLogin />} />
      <Route path="/superadmin" element={<AdminPanel />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
